export const PWA_BROWSER_THEME_COLOR = '#003866'
export const PWA_STANDALONE_THEME_COLOR = '#FFF'
export const PWA_STANDALONE_DISPLAY_MODE = '(display-mode: standalone)'

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

interface NavigationWithHistory {
  canGoBack?: boolean
}

export const isStandalonePwa = () => {
  const navigatorWithStandalone = navigator as NavigatorWithStandalone
  return window.matchMedia?.(PWA_STANDALONE_DISPLAY_MODE).matches || navigatorWithStandalone.standalone === true
}

export const PWA_STANDALONE_THEME_META = {
  key: 'pwa-theme-color',
  name: 'theme-color',
  content: PWA_STANDALONE_THEME_COLOR,
  media: PWA_STANDALONE_DISPLAY_MODE
}

export const canNavigateBack = (isStartRoute: () => boolean) => {
  const navigation = (window as Window & { navigation?: NavigationWithHistory }).navigation

  if (typeof navigation?.canGoBack === 'boolean') {
    return navigation.canGoBack
  }

  return !isStartRoute() && window.history.length > 1
}

// Refresh is a full page reload, so the app is torn down before it can show any progress. Leave a
// one-shot flag behind that the reloaded page reads to display the reload bar (AB#62456 AC4).
export const PWA_RELOAD_FLAG = 'pwaReloadInProgress'

export const refreshCurrentPage = () => {
  try {
    window.sessionStorage.setItem(PWA_RELOAD_FLAG, '1')
  } catch {
    // Private-mode / storage-disabled: fall through to a plain reload without the progress bar.
  }
  window.location.reload()
}

// Reads and clears the reload flag. Returns true once per refresh, on the freshly loaded page.
//
// The only consumer is PwaReloadProgress's onMounted, and that component renders solely from
// BaseLayout behind `v-if="pwaControlsVisible"` -- so consuming the flag is coupled to the
// layout. A reload that lands on a `barebone`/`join` route (neither renders BaseLayout) leaves
// the flag set for the rest of the tab session, and the next navigation that does render the bar
// shows a reload it did not trigger.
//
// Left as-is deliberately. The refresh button only exists where the bar renders, and
// `window.location.reload()` returns to the same URL and therefore the same layout, so the
// residual path is a mid-reload redirect into a barebone route. Nuxt2 has the identical
// structure (components/layout/BaseLayout.vue, same gate, same barebone page set), so changing
// it here would be a parity divergence. Revisit if the reload bar is ever wanted on
// barebone/join routes, or fold the read into BaseLayout's onMounted at that point.
export const consumePwaReloadFlag = () => {
  try {
    if (window.sessionStorage.getItem(PWA_RELOAD_FLAG) === '1') {
      window.sessionStorage.removeItem(PWA_RELOAD_FLAG)
      return true
    }
  } catch {
    // ignore storage access errors
  }
  return false
}
